/**
 * Serve Mode layout a deputy can customize: which panels show and in what order, which columns the paper
 * list shows, day/night, text size. Safety pieces (alerts, OUT OF CAR / ATTEMPT / IN SERVICE) can be moved but
 * never hidden. Anything unknown is dropped; anything missing is added back where it belongs, so an old saved
 * layout keeps working when new panels or columns are added.
 */

export const SERVE_PANELS = {
  top: [
    { id: 'location', label: 'Current location' },
    { id: 'nextstop', label: 'Next stop' },
  ],
  left: [
    { id: 'map', label: 'Map' },
    { id: 'alerts', label: 'Safety alerts', locked: true },
    { id: 'buttons', label: 'Out of car / Attempt / In service', locked: true },
  ],
  right: [
    { id: 'possession', label: 'Papers in my possession (with eligible now / later / not today)' },
    { id: 'today', label: 'Papers attempted today' },
    { id: 'return', label: 'Papers to return' },
    { id: 'pickup', label: 'Papers to pick up' },
    { id: 'messages', label: 'Messages' },
  ],
} as const;

export const SERVE_COLUMNS = [
  { id: 'stop', label: '# (stop order)', default: true },
  { id: 'last', label: 'Last attempt', default: true },
  { id: 'personal', label: '% personal', default: true },
  { id: 'sub', label: '% substitute', default: true },
  { id: 'mi', label: 'Miles', default: true },
  { id: 'court', label: 'Court date', default: false },
  { id: 'case', label: 'Case #', default: false },
  { id: 'type', label: 'Paper type', default: false },
  { id: 'attempts', label: 'Attempts so far', default: false },
] as const;

export type ServeArea = keyof typeof SERVE_PANELS;
export type ServeColumnId = (typeof SERVE_COLUMNS)[number]['id'];
export interface PanelPref {
  id: string;
  hidden: boolean;
}
export interface ServeLayout {
  v: 1;
  top: PanelPref[];
  left: PanelPref[];
  right: PanelPref[];
  /** Visible columns, in order. The paper column itself is always shown. */
  columns: ServeColumnId[];
  mapSide: 'left' | 'right';
  theme: 'day' | 'night';
  textSize: 'normal' | 'large' | 'xlarge';
  showLegend: boolean;
}

export const DEFAULT_SERVE_LAYOUT: ServeLayout = {
  v: 1,
  top: SERVE_PANELS.top.map((p) => ({ id: p.id, hidden: false })),
  left: SERVE_PANELS.left.map((p) => ({ id: p.id, hidden: false })),
  right: SERVE_PANELS.right.map((p) => ({ id: p.id, hidden: false })),
  columns: SERVE_COLUMNS.filter((c) => c.default).map((c) => c.id),
  mapSide: 'left',
  theme: 'day',
  textSize: 'normal',
  showLegend: true,
};

const LOCKED = new Set<string>(Object.values(SERVE_PANELS).flatMap((ps) => ps.filter((p) => 'locked' in p && p.locked).map((p) => p.id)));
export const isLockedPanel = (id: string) => LOCKED.has(id);

function normArea(area: ServeArea, v: unknown): PanelPref[] {
  const known = SERVE_PANELS[area].map((p) => p.id as string);
  const out: PanelPref[] = [];
  if (Array.isArray(v)) {
    for (const x of v) {
      const id = typeof x === 'string' ? x : (x as any)?.id;
      if (typeof id !== 'string' || !known.includes(id) || out.some((o) => o.id === id)) continue;
      out.push({ id, hidden: LOCKED.has(id) ? false : !!(x as any)?.hidden });
    }
  }
  // Anything missing (e.g. a panel added after this layout was saved) goes at the end, visible.
  for (const id of known) if (!out.some((o) => o.id === id)) out.push({ id, hidden: false });
  return out;
}

/** Make any stored value a valid layout (unknown → defaults). */
export function normalizeServeLayout(v: unknown): ServeLayout {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const colIds = SERVE_COLUMNS.map((c) => c.id as string);
  const columns = Array.isArray(o.columns) ? ([...new Set(o.columns.filter((c): c is string => typeof c === 'string' && colIds.includes(c)))] as ServeColumnId[]) : DEFAULT_SERVE_LAYOUT.columns;
  return {
    v: 1,
    top: normArea('top', o.top),
    left: normArea('left', o.left),
    right: normArea('right', o.right),
    columns,
    mapSide: o.mapSide === 'right' ? 'right' : 'left',
    theme: o.theme === 'night' ? 'night' : 'day',
    textSize: o.textSize === 'large' || o.textSize === 'xlarge' ? o.textSize : 'normal',
    showLegend: o.showLegend !== false,
  };
}
